import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, ScanBarcode, X, Check, AlertCircle, FileText, Printer } from 'lucide-react';
import { quotationsAPI, partiesAPI, itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ══════════════════════════════════════════════════════════════════════════════
// BARCODE SCANNER MODAL COMPONENT - Premium Design
// ══════════════════════════════════════════════════════════════════════════════
const BarcodeScannerModal = ({ isOpen, onClose, onScan, items }) => {
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleScan = () => {
    if (!barcode.trim()) return;
    const item = items.find(p => 
      p.code?.toLowerCase() === barcode.toLowerCase() ||
      p.barcode?.toLowerCase() === barcode.toLowerCase() ||
      p.sku?.toLowerCase() === barcode.toLowerCase()
    );
    if (item) {
      onScan(item);
      setSuccess(`Added: ${item.name}`);
      setError('');
      setBarcode('');
      setTimeout(() => setSuccess(''), 2000);
      inputRef.current?.focus();
    } else {
      setError('Item not found. Try again.');
      setSuccess('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleScan(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-backdropFadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden animate-modalSlideIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ScanBarcode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-800">Scan Item</h3>
              <p className="text-[12px] text-slate-500">Scan barcode or type manually</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input ref={inputRef} type="text" value={barcode}
              onChange={(e) => { setBarcode(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              placeholder="Scan barcode or type and press Enter" autoFocus
            />
            <button onClick={handleScan} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Check className="w-4 h-4" />
            </button>
          </div>
          {success && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-[13px] text-emerald-700 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-[13px] text-red-700">{error}</span>
            </div>
          )}
          <p className="mt-4 text-[12px] text-slate-500 text-center">Modal stays open for rapid scanning. Press Enter after each scan.</p>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN CREATE QUOTATION PAGE - Premium Zoho-Style Design (Matching Sales Order)
// ══════════════════════════════════════════════════════════════════════════════
const CreateQuotation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get('customer');
  const { activeBusiness } = useBusiness();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedQuotationId, setSavedQuotationId] = useState(id);
  
  // Data states
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const customerSearchRef = useRef(null);
  const itemSearchRef = useRef(null);

  // Modal states
  const [showScanner, setShowScanner] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  
  // Animation state
  const [newlyAddedIndex, setNewlyAddedIndex] = useState(null);
  
  // Validation
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    doc_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    party: null,
    meta: {
      valid_days: 30,
      reference_no: '',
      place_of_supply: ''
    },
    items: [],
    charges: [],
    totals: {
      subtotal: 0,
      discount_total: 0,
      taxable_amount: 0,
      tax_total: 0,
      round_off: 0,
      grand_total: 0
    },
    tax_breakup: [],
    terms: {
      notes_public: '',
      terms_lines: ['Goods once sold will not be taken back', 'All disputes subject to local jurisdiction']
    },
    enable_round_off: true
  });

  const bid = activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchCustomers();
      fetchProducts();
      if (isEdit) loadQuotation();
    }
  }, [bid, id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.charges, formData.enable_round_off]);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const response = await quotationsAPI.get(id);
      const data = response.data;
      setFormData({
        doc_date: data.doc_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        valid_until: data.meta?.valid_until?.split('T')[0] || '',
        party: data.party,
        meta: data.meta || { valid_days: 30, reference_no: '', place_of_supply: '' },
        items: data.items || [],
        charges: data.charges || [],
        totals: data.totals || {},
        tax_breakup: data.tax_breakup || [],
        terms: data.terms || { notes_public: '', terms_lines: [] },
        enable_round_off: true
      });
      if (data.party) {
        setSelectedCustomer(data.party);
        setCustomerSearch(data.party.name || '');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load quotation'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await partiesAPI.list({ type: 'customer', limit: 100 });
      setCustomers(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load customers'));
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await itemsAPI.list({ limit: 500 });
      setProducts(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load products'));
    }
  };

  // Pre-select customer from query param (e.g. from Customer Detail page)
  useEffect(() => {
    if (!preselectedCustomerId || isEdit || selectedCustomer) return;
    const preselectCustomer = async () => {
      const match = customers.find(c => (c.party_id || c.id) === preselectedCustomerId);
      if (match) { handleCustomerSelect(match); return; }
      if (customers.length > 0) {
        try {
          const { data } = await partiesAPI.get(preselectedCustomerId);
          const party = data.party || data;
          if (party?.name) handleCustomerSelect({ id: party.id, party_id: party.id, name: party.name, phone: party.phone, gstin: party.gstin });
        } catch (e) { console.warn('Could not fetch preselected customer:', e); }
      }
    };
    preselectCustomer();
  }, [preselectedCustomerId, customers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({
      ...prev,
      party: {
        party_id: customer.id,
        name: customer.name,
        phone: customer.phone,
        email: customer.email || '',
        gstin: customer.gstin,
        billing_address: customer.billing_address || customer.address,
        shipping_address: customer.shipping_address
      }
    }));
    setValidationErrors(prev => ({ ...prev, customer: null }));
  };

  const addItem = (product = null) => {
    if (product) {
      // Check if item already exists
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'qty', (formData.items[existingIndex].qty || 1) + 1);
        return;
      }
      
      const price = product.selling_price || product.sales_price || product.pricing?.sales_price || 0;
      const taxRate = product.tax || product.tax_rate || 18;
      const lineTotal = 1 * price;
      const taxAmt = lineTotal * (taxRate / 100);
      
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        
        return {
          ...prev,
          items: [...prev.items, {
            line_no: prev.items.length + 1,
            item_id: product.id,
            name: product.name,
            description: '',
            hsn_sac: product.hsn || product.hsn_sac || '',
            unit: product.unit || product.primary_unit || 'PCS',
            qty: 1,
            price: price,
            discount_type: 'PERCENT',
            discount_value: 0,
            tax_rate: taxRate,
            tax_inclusive: false,
            line_subtotal: Math.round(lineTotal * 100) / 100,
            line_tax: Math.round(taxAmt * 100) / 100,
            line_total: Math.round((lineTotal + taxAmt) * 100) / 100
          }]
        };
      });
    } else {
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex);
        setTimeout(() => setNewlyAddedIndex(null), 400);
        
        return {
          ...prev,
          items: [...prev.items, {
            line_no: prev.items.length + 1,
            item_id: null,
            name: '',
            description: '',
            hsn_sac: '',
            unit: 'PCS',
            qty: 1,
            price: 0,
            discount_type: 'PERCENT',
            discount_value: 0,
            tax_rate: 18,
            tax_inclusive: false,
            line_subtotal: 0,
            line_tax: 0,
            line_total: 0
          }]
        };
      });
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index).map((item, idx) => ({
        ...item,
        line_no: idx + 1
      }))
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      // If selecting a product from dropdown
      if (field === 'item_id' && value) {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].name = product.name;
          newItems[index].hsn_sac = product.hsn || product.hsn_sac || '';
          newItems[index].price = product.selling_price || product.sales_price || 0;
          newItems[index].unit = product.unit || product.primary_unit || 'PCS';
          newItems[index].tax_rate = product.tax || product.tax_rate || 18;
        }
      }

      // Recalculate line amounts
      const qty = parseFloat(newItems[index].qty) || 0;
      const price = parseFloat(newItems[index].price) || 0;
      const discountValue = parseFloat(newItems[index].discount_value) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;

      const lineSubtotal = qty * price;
      let discount = 0;
      if (newItems[index].discount_type === 'PERCENT') {
        discount = lineSubtotal * (discountValue / 100);
      } else {
        discount = discountValue;
      }

      const taxable = lineSubtotal - discount;
      const tax = taxable * (taxRate / 100);

      newItems[index].line_subtotal = Math.round(lineSubtotal * 100) / 100;
      newItems[index].discount_amount = Math.round(discount * 100) / 100;
      newItems[index].line_tax = Math.round(tax * 100) / 100;
      newItems[index].line_total = Math.round((taxable + tax) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let discountTotal = 0;
    let taxTotal = 0;
    const taxMap = {};

    formData.items.forEach(item => {
      subtotal += item.line_subtotal || 0;
      const itemDiscount = item.discount_type === 'PERCENT' 
        ? (item.line_subtotal || 0) * ((item.discount_value || 0) / 100)
        : (item.discount_value || 0);
      discountTotal += itemDiscount;
      taxTotal += item.line_tax || 0;

      if (item.tax_rate > 0) {
        if (!taxMap[item.tax_rate]) {
          taxMap[item.tax_rate] = { taxable: 0, tax: 0 };
        }
        const taxableAmount = (item.line_subtotal || 0) - itemDiscount;
        taxMap[item.tax_rate].taxable += taxableAmount;
        taxMap[item.tax_rate].tax += item.line_tax || 0;
      }
    });

    const chargesTotal = formData.charges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
    const taxableAmount = subtotal - discountTotal + chargesTotal;
    const grandTotal = taxableAmount + taxTotal;
    const roundOff = formData.enable_round_off ? Math.round(grandTotal) - grandTotal : 0;

    const tax_breakup = Object.keys(taxMap).map(rate => ({
      tax_rate: parseFloat(rate),
      taxable: Math.round(taxMap[rate].taxable * 100) / 100,
      tax: Math.round(taxMap[rate].tax * 100) / 100
    }));

    setFormData(prev => ({
      ...prev,
      totals: {
        subtotal: Math.round(subtotal * 100) / 100,
        discount_total: Math.round(discountTotal * 100) / 100,
        taxable_amount: Math.round(taxableAmount * 100) / 100,
        tax_total: Math.round(taxTotal * 100) / 100,
        round_off: Math.round(roundOff * 100) / 100,
        grand_total: Math.round((grandTotal + roundOff) * 100) / 100
      },
      tax_breakup
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.party) {
      errors.customer = 'Customer is required';
    }
    
    if (!formData.doc_date) {
      errors.doc_date = 'Quote date is required';
    }
    
    if (formData.items.length === 0) {
      errors.items = 'At least one item is required';
    } else {
      formData.items.forEach((item, index) => {
        if (!item.name || !item.name.trim()) {
          errors[`item_${index}_name`] = 'Item name required';
        }
        if (!item.qty || item.qty <= 0) {
          errors[`item_${index}_qty`] = 'Quantity must be > 0';
        }
      });
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (status = 'DRAFT') => {
    setValidationErrors({});
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        doc_type: 'QUOTATION',
        doc_date: new Date(formData.doc_date).toISOString(),
        party: formData.party,
        meta: {
          ...formData.meta,
          valid_until: formData.valid_until
            ? new Date(formData.valid_until).toISOString()
            : new Date(Date.now() + (formData.meta.valid_days || 30) * 24 * 60 * 60 * 1000).toISOString()
        },
        items: formData.items,
        charges: formData.charges,
        totals: formData.totals,
        tax_breakup: formData.tax_breakup,
        terms: formData.terms,
        status,
        currency: 'INR'
      };

      let quotationId;
      if (isEdit || savedQuotationId) {
        await quotationsAPI.update(savedQuotationId || id, payload);
        toast.success('Quotation updated successfully!');
        quotationId = savedQuotationId || id;
      } else {
        const response = await quotationsAPI.create(payload);
        toast.success('Quotation created successfully!');
        quotationId = response.data.id;
        setSavedQuotationId(quotationId);
      }

      const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
      setTimeout(() => navigate(`${businessPath}/sales/quotations`), 1000);
    } catch (error) {
      // Route through getApiErrorMessage so Pydantic 422 array responses
      // don't crash React with "Objects are not valid as a React child".
      toast.error(getApiErrorMessage(error, 'Failed to save quotation'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!savedQuotationId && !id) {
      toast.error('Save quotation first before printing');
      return;
    }
    setShowPrintPreview(true);
  };

  const handleItemCreated = (newItem) => {
    addItem(newItem);
    toast.success('Item added to quotation!');
  };

  const handleBarcodeScan = async (barcode) => {
    try {
      setShowScanner(false);
      toast.loading('Looking up item...', { id: 'barcode-lookup' });
      
      const response = await itemsAPI.lookupByBarcode(barcode);
      toast.success('Item found!', { id: 'barcode-lookup' });
      addItem(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Item not found. Create it now?', { id: 'barcode-lookup' });
        setScannedBarcode(barcode);
        setShowQuickAdd(true);
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to lookup item'), { id: 'barcode-lookup' });
      }
    }
  };

  // Filter functions
  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB]" data-testid="create-quotation-page">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design (Matching Sales Order)
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
                  navigate(`${businessPath}/sales/quotations`);
                }}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEdit ? 'Edit Quotation' : 'New Quotation'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Create and manage customer quotes</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              <button 
                onClick={handlePrint}
                disabled={!savedQuotationId && !id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="print-preview-btn"
              >
                <Printer className="w-4 h-4" /> Preview
              </button>
              <button 
                onClick={() => handleSave('DRAFT')} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                data-testid="save-draft-btn"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button 
                onClick={() => handleSave('SENT')} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
                data-testid="confirm-quotation-btn"
              >
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Confirm Quotation'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA - Premium Layout (12-column grid)
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">
              
              {/* CUSTOMER DETAILS Card - Premium Style */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <Search className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Customer Details</h2>
                    <p className="text-[11px] text-slate-500">Search and select customer</p>
                  </div>
                </div>
                
                <div className="relative" ref={customerSearchRef}>
                  <label className={`block text-[12px] font-medium mb-2 ${validationErrors.customer ? 'text-red-500' : 'text-slate-600'}`}>
                    Customer {validationErrors.customer && <span className="text-red-500 ml-1">• Required</span>}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50 border-2 rounded-xl text-[14px] focus:bg-white focus:ring-0 focus:outline-none transition-all duration-200 ${
                        validationErrors.customer 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-slate-200 focus:border-blue-500'
                      }`}
                      placeholder="Search by name, phone, or email..."
                      data-testid="customer-search-input"
                    />
                  </div>
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredCustomers.slice(0, 8).map((customer, idx) => {
                        const contact = [customer.phone, customer.email].filter(Boolean).join(' • ');
                        return (
                          <div key={customer.id}
                            onClick={() => handleCustomerSelect(customer)}
                            className={`px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{customer.name}</p>
                              {contact && <p className="text-[12px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedCustomer && (() => {
                  const selContact = [selectedCustomer.phone, selectedCustomer.email || selectedCustomer.gstin].filter(Boolean).join(' • ');
                  return (
                    <div className="mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedCustomer.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerSearch('');
                            setFormData(prev => ({ ...prev, party: null }));
                          }}
                          className="text-slate-400 hover:text-slate-600 shrink-0"
                          data-testid="clear-customer-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS Card - Premium Design */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Order Items</h2>
                      <p className="text-[11px] text-slate-500">{formData.items.length} item{formData.items.length !== 1 ? 's' : ''} added</p>
                    </div>
                  </div>
                </div>

                {/* Quick Add Row - Premium */}
                <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-[14px] focus:border-purple-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search items by name or code..."
                      data-testid="item-search-input"
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map((product, idx) => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className={`px-4 py-3 hover:bg-purple-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}
                            data-testid={`item-option-${product.id}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[12px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => setShowScanner(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                    data-testid="scan-barcode-btn"
                  >
                    <ScanBarcode className="w-4 h-4" /> Scan
                  </button>
                  <button 
                    onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                    data-testid="add-line-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table - Premium */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50" data-testid="no-items-placeholder">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[14px] font-medium text-slate-600">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Search above or click &quot;Add Line&quot; to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full" data-testid="items-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">HSN/SAC</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Price</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Disc %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                          <th className="px-4 py-3 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formData.items.map((item, index) => (
                          <tr 
                            key={`item-${index}-${item.item_id || 'new'}`} 
                            className={`hover:bg-slate-50/80 transition-colors duration-150 ${
                              index === newlyAddedIndex ? 'animate-lineItemSlideIn bg-purple-50/50' : ''
                            }`}
                            data-testid={`item-row-${index}`}
                          >
                            <td className="px-4 py-3 text-[13px] text-slate-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              {item.item_id ? (
                                <input 
                                  type="text" 
                                  value={item.name}
                                  onChange={(e) => updateItem(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-purple-500 focus:bg-white transition-all"
                                />
                              ) : (
                                <select 
                                  value={item.item_id || ''} 
                                  onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                  className={`w-full px-3 py-2 bg-slate-50 border rounded-lg text-[13px] focus:outline-none focus:border-purple-500 focus:bg-white transition-all ${
                                    validationErrors[`item_${index}_name`] ? 'border-red-300' : 'border-slate-200'
                                  }`}
                                >
                                  <option value="">Select Item</option>
                                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="text" 
                                value={item.hsn_sac || ''} 
                                onChange={(e) => updateItem(index, 'hsn_sac', e.target.value)}
                                className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all" 
                                placeholder="—" 
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="number" 
                                value={item.qty} 
                                onChange={(e) => updateItem(index, 'qty', parseFloat(e.target.value) || 0)}
                                className={`w-16 px-3 py-2 bg-slate-50 border rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all ${
                                  validationErrors[`item_${index}_qty`] ? 'border-red-300' : 'border-slate-200'
                                }`}
                                min="0" 
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="text" 
                                value={item.unit} 
                                onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all" 
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="number" 
                                value={item.price} 
                                onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                                className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" 
                                min="0" 
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="number" 
                                value={item.discount_value} 
                                onChange={(e) => updateItem(index, 'discount_value', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" 
                                min="0" 
                                max="100" 
                                placeholder="%" 
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input 
                                type="number" 
                                value={item.tax_rate} 
                                onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" 
                                min="0" 
                                placeholder="%" 
                              />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{formatCurrency(item.line_total)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button 
                                onClick={() => removeItem(index)} 
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 active:scale-95"
                                data-testid={`remove-item-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* TERMS & NOTES Card - Premium */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Terms & Notes</h2>
                    <p className="text-[11px] text-slate-500">Additional information</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Public Notes (visible on order)</label>
                    <textarea 
                      value={formData.terms.notes_public}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        terms: { ...prev.terms, notes_public: e.target.value } 
                      }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                      rows={3} 
                      placeholder="Add notes for customer..."
                      data-testid="notes-textarea"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-12 lg:col-span-4 space-y-4">
              
              {/* QUOTE DETAILS Card - Premium Style */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Details</h2>
                    <p className="text-[11px] text-slate-500">Configure order settings</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Order Number</label>
                    <input 
                      type="text" 
                      value={savedQuotationId ? `QT-${savedQuotationId}` : 'Auto-generated'} 
                      disabled
                      className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-[14px] text-slate-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-2">Order Date</label>
                      <div className="relative">
                        <input 
                          type="date" 
                          value={formData.doc_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, doc_date: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                          data-testid="quote-date-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Valid Until</label>
                      <input 
                        type="date" 
                        value={formData.valid_until}
                        onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                        data-testid="valid-until-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Place of Supply</label>
                    <input 
                      type="text" 
                      value={formData.meta.place_of_supply}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        meta: { ...prev.meta, place_of_supply: e.target.value } 
                      }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="State (for tax calculation)"
                      data-testid="place-of-supply-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reference / PO Number</label>
                    <input 
                      type="text" 
                      value={formData.meta.reference_no}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        meta: { ...prev.meta, reference_no: e.target.value } 
                      }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Customer PO or reference"
                      data-testid="reference-input"
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY Card - Premium Green Gradient */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Summary</h2>
                    <p className="text-[11px] text-slate-500">Final amounts</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{formatCurrency(formData.totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Taxable Amount</span>
                    <span className="text-slate-800">₹{formatCurrency(formData.totals.taxable_amount)}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600 font-medium">+ ₹{formatCurrency(formData.totals.tax_total)}</span>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[20px] font-bold text-emerald-600" data-testid="grand-total">
                        ₹{formatCurrency(formData.totals.grand_total)}
                      </span>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      checked={formData.enable_round_off}
                      onChange={(e) => setFormData(prev => ({ ...prev, enable_round_off: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      data-testid="round-off-checkbox"
                    />
                    <span className="text-[12px] text-slate-600 group-hover:text-slate-800 transition-colors">Enable round off</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal (Premium) */}
      <BarcodeScannerModal 
        isOpen={showScanner} 
        onClose={() => setShowScanner(false)}
        onScan={(item) => addItem(item)} 
        items={products}
      />

      {/* Quick Add Item Modal */}
      {showQuickAdd && (
        <QuickAddItemModal
          barcode={scannedBarcode}
          onClose={() => {
            setShowQuickAdd(false);
            setScannedBarcode('');
          }}
          onItemCreated={handleItemCreated}
        />
      )}

      {/* Print Preview Modal */}
      {showPrintPreview && (savedQuotationId || id) && (
        <PrintPreviewModal
          documentId={savedQuotationId || id}
          document={{
            ...formData,
            quotation_no: `QT-${savedQuotationId || id}`,
            doc_type: 'QUOTATION'
          }}
          documentType="QUOTATION"
          onClose={() => setShowPrintPreview(false)}
        />
      )}
    </div>
  );
};

export default CreateQuotation;
